import {
    Button,
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Flex,
    GridItem,
    SimpleGrid,
    Spinner,
    Text,
    useMediaQuery,
    useDisclosure,
    Box,
    Table,
    Tbody,
    Td,
    Tr,
    useRadioGroup,
    useRadio,
    useColorModeValue,
    VStack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Badge,
    Icon,
    HStack,
    Divider,
} from "@chakra-ui/react";
import { TbMapPin, TbSettings, TbMap, TbChartDots3, TbStar, TbStarFilled, TbFilter } from "react-icons/tb";
import moment from "moment";
import StationsApi from "Api/StationsApi";
import { divIcon } from "leaflet";
import { useCallback, useEffect, useState } from "react";
import { TbExternalLink } from "react-icons/tb";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import { Link } from "react-router-dom";
import Favorites from "Helpers/Favorites";

export function renderStationMarkers(stations, mapMarkerType) {
    return (stations || []).map((station) => {
        let activeData = getActiveData(station, mapMarkerType);

        let color = getCor(activeData, mapMarkerType);

        return <Marker
            icon={divIcon({
                iconSize: [0, 0],
                html: `<div style="background-color: ${color}; color:white;" class="icone icone-${color.replace('#', '')}">${activeData ?? "-"}</div>`,
                popupAnchor: [5, 0],
                className: `${activeData ?? "x null"}`,
            })}
            key={"marker_" + station.id}
            position={[station.lat, station.long]}
        >
            <Popup>
                <Box className="station-popup" minW="280px" maxW="320px">
                    {/* Station Header */}
                    <Box
                        bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                        color="white"
                        p={3}
                        borderRadius="lg"
                        mb={3}
                        textAlign="center"
                    >
                        <Text
                            fontWeight="bold"
                            fontSize="sm"
                            lineHeight="1.3"
                            m="0 !important"
                        >
                            {station.full_name
                                ? station.full_name
                                : `${station.city} / ${station.state}`}
                        </Text>
                        <Text fontSize="xs" opacity={0.8} m="0 !important">
                            {station.provider} - {station.identifier}
                        </Text>
                    </Box>

                    {/* Weather Data Grid */}
                    <SimpleGrid columns={3} gap={2} mb={3}>
                        {station.last_data.temp_min != null && (
                            <Box
                                bg="blue.50"
                                p={2}
                                borderRadius="lg"
                                textAlign="center"
                            >
                                <Text fontWeight="bold" color="blue.600" fontSize="md" m="0 !important">
                                    {station.last_data.temp_min}º
                                </Text>
                                <Text fontSize="xs" color="blue.500" m="0 !important">Mínima</Text>
                            </Box>
                        )}

                        {station.last_data.temp != null && (
                            <Box
                                bg="gray.100"
                                p={2}
                                borderRadius="lg"
                                textAlign="center"
                            >
                                <Text fontWeight="bold" color="gray.700" fontSize="md" m="0 !important">
                                    {station.last_data.temp}º
                                </Text>
                                <Text fontSize="xs" color="gray.600" m="0 !important">Atual</Text>
                            </Box>
                        )}

                        {station.last_data.temp_max != null && (
                            <Box
                                bg="red.50"
                                p={2}
                                borderRadius="lg"
                                textAlign="center"
                            >
                                <Text fontWeight="bold" color="red.500" fontSize="md" m="0 !important">
                                    {station.last_data.temp_max}º
                                </Text>
                                <Text fontSize="xs" color="red.400" m="0 !important">Máxima</Text>
                            </Box>
                        )}

                        {station.last_data.wind_speed != null && (
                            <Box
                                bg="green.50"
                                p={2}
                                borderRadius="lg"
                                textAlign="center"
                            >
                                <Text fontWeight="bold" color="green.600" fontSize="sm" m="0 !important">
                                    {station.last_data.wind_speed} km/h
                                </Text>
                                <Text fontSize="xs" color="green.500" m="0 !important">Vento</Text>
                            </Box>
                        )}

                        {station.last_data.wind_dir != null && (
                            <Box
                                bg="green.50"
                                p={2}
                                borderRadius="lg"
                                textAlign="center"
                            >
                                <Text fontWeight="bold" color="green.600" fontSize="sm" m="0 !important">
                                    {station.last_data.wind_dir}º ({degreesToCardinal(station.last_data.wind_dir)})
                                </Text>
                                <Text fontSize="xs" color="green.500" m="0 !important">Direção</Text>
                            </Box>
                        )}

                        {station.last_data.wind_gust != null && (
                            <Box
                                bg="green.50"
                                p={2}
                                borderRadius="lg"
                                textAlign="center"
                            >
                                <Text fontWeight="bold" color="green.600" fontSize="sm" m="0 !important">
                                    {station.last_data.wind_gust} km/h
                                </Text>
                                <Text fontSize="xs" color="green.500" m="0 !important">Rajada</Text>
                            </Box>
                        )}

                        {station.last_data.humidity != null && (
                            <Box
                                bg="cyan.50"
                                p={2}
                                borderRadius="lg"
                                textAlign="center"
                            >
                                <Text fontWeight="bold" color="cyan.600" fontSize="md" m="0 !important">
                                    {station.last_data.humidity}%
                                </Text>
                                <Text fontSize="xs" color="cyan.500" m="0 !important">Umidade</Text>
                            </Box>
                        )}

                        {station.last_data.precip_rate != null && (
                            <Box
                                bg="blue.50"
                                p={2}
                                borderRadius="lg"
                                textAlign="center"
                            >
                                <Text fontWeight="bold" color="blue.600" fontSize="sm" m="0 !important">
                                    {station.last_data.precip_rate} mm
                                </Text>
                                <Text fontSize="xs" color="blue.500" m="0 !important">Chuva</Text>
                            </Box>
                        )}

                        {station.last_data.precip_total != null && (
                            <Box
                                bg="blue.50"
                                p={2}
                                borderRadius="lg"
                                textAlign="center"
                            >
                                <Text fontWeight="bold" color="blue.600" fontSize="sm" m="0 !important">
                                    {station.last_data.precip_total} mm
                                </Text>
                                <Text fontSize="xs" color="blue.500" m="0 !important">Acumulado</Text>
                            </Box>
                        )}
                    </SimpleGrid>

                    {/* Footer */}
                    <Box
                        borderTop="1px"
                        borderColor="gray.200"
                        pt={2}
                        textAlign="center"
                    >
                        <Text fontSize="xs" color="gray.500" mb={2} m="0 0 8px 0 !important">
                            Atualizado {station.last_data.created_at}
                        </Text>
                        <Link to={`/estacao/${station.id}`}>
                            <Button
                                size="sm"
                                bg="#2D6B3F"
                                color="white"
                                _hover={{ bg: "#1F5A2F" }}
                                rightIcon={<TbExternalLink />}
                                borderRadius="lg"
                                w="full"
                            >
                                Ver mais detalhes
                            </Button>
                        </Link>
                    </Box>
                </Box>
            </Popup>
        </Marker>
    })
}


export function corTemperatura(valor) {
    valor = Number(valor);

    if (valor <= -15) {
        return "#ED64A6";
    } else if (valor <= -5) {
        return "#B758B3"; // "#97266D";
    } else if (valor < 0) {
        return "#694295"; // "#805AD5";
    } else if (valor < 5) {
        return "#2D458F"; // "#2B6CB0";
    } else if (valor < 10) {
        return "#2682C5"; // "#00B5D8";
    } else if (valor < 15) {
        return "#4FAEAD"; // "#38B2AC";
    } else if (valor < 20) {
        return "#51A14F"; // "#48BB78";
    } else if (valor < 25) {
        return "#94BD3A"; // "#68D391";
    } else if (valor < 30) {
        return "#EAB735"; // "#F6E05E";
    } else if (valor < 35) {
        return "#EC743B"; // "#ED8936";
    } else if (valor < 40) {
        return "#CE3C35"; // "#E53E3E";
    } else if (valor < 45) {
        return "#673721"; // "#9B2C2C";
    } else {
        return "#1F1F1F"; // "#652B19";
    }
}

export function corUmidade(valor) {
    valor = Number(valor);
    if (valor <= 10) {
        return "#1F1F1F";
    } else if (valor <= 20) {
        return "#673721"; // "#9C4221";
    } else if (valor <= 30) {
        return "#942823"; // "#9B2C2C";
    } else if (valor <= 40) {
        return "#CE3C35"; // "#E53E3E";
    } else if (valor <= 50) {
        return "#DF9D9D"; // "#F6AD55";
    } else if (valor <= 60) {
        return "#DDDDDD"; // "#90CDF4";
    } else if (valor <= 70) {
        return "#9EC5E1"; // "#4299E1";
    } else if (valor <= 80) {
        return "#1673C2"; // "#2B6CB0";
    } else if (valor <= 90) {
        return "#2D458F"; // "#2C5282";
    } else {
        return "#694295"; // "#6B46C1";
    }
}

function corElevacao(valor) {
    valor = Number(valor);
    if (valor <= 0) {
        return "#575757";
    } else if (valor <= 20) {
        return "#2D458F";
    } else if (valor <= 50) {
        return "#2682C5";
    } else if (valor <= 100) {
        return "#4FAEAD";
    } else if (valor <= 200) {
        return "#51A14F";
    } else if (valor <= 400) {
        return "#EAB735";
    } else if (valor <= 600) {
        return "#EC743B";
    } else if (valor <= 800) {
        return "#CE3C35";
    } else if (valor <= 1000) {
        return "#DF9D9D";
    } else {
        return "#333333";
    }
}

export function corChuva(valor) {
    valor = Number(valor);

    if (valor <= 0) {
        return "#AAAAAA"; // "#AAAAAA";
    } else if (valor <= 1) {
        return "#EC743B"; // "#63B3ED";
    } else if (valor <= 5) {
        return "#EAB735"; // "#4299E1";
    } else if (valor <= 10) {
        return "#51A14F"; // "#3182CE";
    } else if (valor <= 20) {
        return "#4FAEAD"; // "#2B6CB0";
    } else if (valor <= 30) {
        return "#2682C5"; // "#2C5282";
    } else if (valor <= 50) {
        return "#2D458F"; // "#2A4365";
    } else if (valor <= 100) {
        return "#694295"; // "#1A365D";
    } else if (valor <= 150) {
        return "#575757"; // "#44337A";
    } else {
        return "#1F1F1F"; // "#322659";
    }
}

export function corVento(valor) {
    valor = Number(valor);

    if (valor <= 0) {
        return "#AAAAAA"; // No wind
    } else if (valor <= 5) {
        return "#A8DADC"; // Light breeze
    } else if (valor <= 10) {
        return "#457B9D"; // Gentle breeze
    } else if (valor <= 20) {
        return "#1D3557"; // Moderate wind
    } else if (valor <= 30) {
        return "#F4A261"; // Strong wind
    } else if (valor <= 50) {
        return "#E76F51"; // Very strong wind
    } else {
        return "#D62828"; // Extreme wind
    }
}

function degreesToCardinal(dir) {
    let directions = [
        "N",
        "NNE",
        "NE",
        "ENE",
        "E",
        "ESE",
        "SE",
        "SSE",
        "S",
        "SSW",
        "SW",
        "WSW",
        "W",
        "WNW",
        "NW",
        "NNW",
        "N",
    ];
    return directions[Math.round(dir / 22.5)];
}

const markerOptions = {
    'temp': "Temp.",
    'temp_min': "Temp. Mín.",
    'temp_max': "Temp. Máx.",
    'amplitude': "Amplitude",
    'heat_index': "Sensação",
    'heat_index_min': "Sens. Mín.",
    'heat_index_max': "Sens. Máx.",
    'dew_point': "PTO",
    'dew_point_min': "PTO Mín.",
    'dew_point_max': "PTO Máx.",
    'humidity': "Umidade",
    'humidity_min': "Umidade Mín.",
    'humidity_max': "Umidade Máx.",
    'wind_speed': "Vel. Vento",
    'wind_gust_max': "Rajada Máx.",
    'precip_rate': "Índice de Chuva",
    'precip_total': "Chuva dia",
    'rain_2_days': "Chuva 2 dias",
    'rain_3_days': "Chuva 3 dias",
    'rain_4_days': "Chuva 4 dias",
    'rain_5_days': "Chuva 5 dias",
    'rain_7_days': "Chuva 7 dias",
    'rain_30_days': "Chuva 30 dias",
    'rain_month': "Chuva no mês",
};

const markerOptionsColorType = {
    'temp': "temperatura",
    'temp_min': "temperatura",
    'temp_max': "temperatura",
    'amplitude': "temperatura",
    'heat_index': "temperatura",
    'heat_index_min': "temperatura",
    'heat_index_max': "temperatura",
    'dew_point': "temperatura",
    'dew_point_min': "temperatura",
    'dew_point_max': "temperatura",
    'humidity': "umidade",
    'humidity_min': "umidade",
    'humidity_max': "umidade",
    'wind_speed': "vento",
    'wind_gust_max': "vento",
    'precip_rate': "chuva",
    'precip_total': "chuva",
    'rain_2_days': "chuva",
    'rain_3_days': "chuva",
    'rain_4_days': "chuva",
    'rain_5_days': "chuva",
    'rain_7_days': "chuva",
    'rain_30_days': "chuva",
    'rain_month': "chuva",
};

export function getCor(value, mapMarkerType) {
    let preDefinedColor = markerOptionsColorType[mapMarkerType];

    if (preDefinedColor) {
        switch (preDefinedColor) {
            case "temperatura":
                return corTemperatura(value);
            case "umidade":
                return corUmidade(value);
            case "vento":
                return corVento(value);
            case "chuva":
                return corChuva(value);
            default:
                return "red";
        }
    }

    switch (mapMarkerType) {
        case "temp":
        case "temp_max":
        case "temp_min":
            return corTemperatura(value);
        case "humidity":
            return corUmidade(value);
        case "elevation":
            return corElevacao(value);
        case "precip_rate":
        case "precip_total":
            return corChuva(value);
        default:
            return "red";
    }
}


function getActiveData(station, mapMarkerType) {
    switch (mapMarkerType) {
        case "elevation":
            return station.elevation;
        default:
            return station.last_data[mapMarkerType] ?? null;
    }
}

const mapLayerOptions = {
    'default': "Padrão",
    'satelite': "Satélite",
};


const stationFilterOptions = {
    'all': "Todas as Estações",
    'favorites': "Apenas Favoritos",
};

export default function Home() {
    const [stations, setStations] = useState([]);
    const [mapMarkerType, setMapMarkerType] = useState(localStorage.getItem("mapMarkerType") ?? "temp");
    const [mapLayerType, setMapLayerType] = useState(localStorage.getItem("mapLayerType") ?? "default");
    // Always use ungroup - grouping is disabled
    const mapGroupBehavior = "ungroup";
    const [stationFilter, setStationFilter] = useState(localStorage.getItem("stationFilter") ?? "all");
    const [favorites, setFavorites] = useState(Favorites.get());
    const [isLoadingStations, setIsLoadingStations] = useState(true);
    const [latestStations, setLatestStations] = useState([]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const latestStationsModal = useDisclosure();

    // Toggle favorite for a station
    const toggleFavorite = (station) => {
        const identifier = station.identifier;
        let newFavorites;
        if (favorites.includes(identifier)) {
            newFavorites = favorites.filter((f) => f !== identifier);
            Favorites.remove(identifier);
        } else {
            newFavorites = [...favorites, identifier];
            Favorites.add(identifier);
        }
        setFavorites(newFavorites);
    };

    // Check if station is favorite
    const isFavorite = (station) => {
        return favorites.includes(station.identifier);
    };

    // Get filtered stations
    const getFilteredStations = () => {
        if (stationFilter === 'favorites') {
            return stations.filter(station => favorites.includes(station.identifier));
        }
        return stations;
    };

    const drawerBgColor = useColorModeValue("white", "gray.700")

    // Color mode values for modal (moved outside callbacks)
    const modalHeaderBg = useColorModeValue("brand.600", "brand.700")
    const modalBodyBg = useColorModeValue("white", "gray.800")
    const stationCardBorder = useColorModeValue("gray.200", "gray.700")
    const stationCardBg = useColorModeValue("gray.50", "gray.700")
    const stationCardHoverBg = useColorModeValue("brand.50", "gray.600")
    const stationNameColor = useColorModeValue("brand.600", "brand.300")
    const stationSubtitleColor = useColorModeValue("gray.500", "gray.400")

    // Color mode values for drawer sections
    const sectionTitleColor = useColorModeValue("gray.700", "gray.200")
    const dividerColor = useColorModeValue("gray.200", "gray.600")
    const drawerFooterBorder = useColorModeValue("gray.200", "gray.700")

    let center = [-14.4095261, -51.31668];

    // Check if we should show the latest stations popup (once per day)
    const checkAndShowLatestStationsPopup = async () => {
        const lastShown = localStorage.getItem("latestStationsPopupLastShown");
        const today = moment().format("YYYY-MM-DD");

        if (lastShown !== today) {
            // Load latest stations and show popup
            try {
                const response = await StationsApi.latest();
                if (response.status === 200 && response.json?.length > 0) {
                    setLatestStations(response.json);
                    latestStationsModal.onOpen();
                }
            } catch (err) {
                console.error("Error loading latest stations:", err);
            }
        }
    };

    const handleCloseLatestStationsModal = () => {
        // Save today's date so it won't show again for 24h
        localStorage.setItem("latestStationsPopupLastShown", moment().format("YYYY-MM-DD"));
        latestStationsModal.onClose();
    };

    useEffect(() => {
        loadStations();
        checkAndShowLatestStationsPopup();
    }, []);

    useEffect(() => {
        var channel = window.pusher.subscribe("stations");
        channel.bind("stations-update", function () {
            loadStations();
        });

        return () => {
            window.pusher.unsubscribe("stations");
        };
    }, []);

    async function loadStations() {
        setIsLoadingStations(true);
        let data = await StationsApi.homepage();
        // Dedupe by id so a station repeated in the payload doesn't corrupt keyed marker reconciliation
        const byId = new Map();
        (data.json || []).forEach((station) => byId.set(station.id, station));
        setStations(Array.from(byId.values()));
        setIsLoadingStations(false);
    }

    const [isMobile] = useMediaQuery("(max-width: 768px)");

    function getLabel() {
        return markerOptions[mapMarkerType] || "-";
    }

    useEffect(() => {
        // Store the selected options in localStorage
        localStorage.setItem("mapMarkerType", mapMarkerType);
        localStorage.setItem("mapLayerType", mapLayerType);
        localStorage.setItem("stationFilter", stationFilter);

        onClose();
    }, [mapMarkerType, mapLayerType, stationFilter]);

    const { getRootProps: markerTypeGetRootProps, getRadioProps: markerTypeGetRadioProps } = useRadioGroup({
        name: 'marker_type',
        defaultValue: mapMarkerType,
        onChange: setMapMarkerType,
    })
    const markerTypeGroup = markerTypeGetRootProps();

    const { getRootProps: layerGetRootProps, getRadioProps: layerGetRadioProps } = useRadioGroup({
        name: 'layer_type',
        defaultValue: mapLayerType,
        onChange: setMapLayerType,
    })
    const layerGroup = layerGetRootProps();

    const { getRootProps: filterGetRootProps, getRadioProps: filterGetRadioProps } = useRadioGroup({
        name: 'station_filter',
        defaultValue: stationFilter,
        onChange: setStationFilter,
    })
    const filterGroup = filterGetRootProps();


    const drawClusterIcon = useCallback(function (cluster) {
        let data = [];
        const childMarkers = cluster.getAllChildMarkers();

        let currentDataType = document.querySelector('.current-data .checked-data input')?.value ?? localStorage.getItem("mapMarkerType") ?? "temp";

        childMarkers.forEach((childMarker) => {
            let value = childMarker.options.icon.options.className;

            if (value != "x" && !isNaN(Number(value))) {
                data.push(Number(value));
            }
        });

        let avg = (
            data.reduce((a, b) => a + b, 0) / data.length
        ).toFixed(1);

        let color = getCor(avg, currentDataType);

        return divIcon({
            iconSize: [50, 30],
            html: `<div style="background-color: ${color}; color:white;" class="icone icone-cluster icone-${color.replace('#', '')}">~${avg}<br>${cluster._childCount} estações</div>`,
            popupAnchor: [60, 0],
        });
    }, [mapMarkerType, mapGroupBehavior, stations])

    // Render station markers with favorite button in popup
    const renderStationMarkersWithFavorites = useCallback((stationsToRender) => {
        return (stationsToRender || []).map((station) => {
            let activeData = getActiveData(station, mapMarkerType);
            let color = getCor(activeData, mapMarkerType);
            const stationIsFavorite = isFavorite(station);

            return <Marker
                icon={divIcon({
                    iconSize: [0, 0],
                    html: `<div style="background-color: ${color}; color:white;" class="icone icone-${color.replace('#', '')}">${activeData ?? "-"}</div>`,
                    popupAnchor: [5, 0],
                    className: `${activeData ?? "x null"}`,
                })}
                key={"marker_" + station.id}
                position={[station.lat, station.long]}
            >
                <Popup>
                    <Box className="station-popup" minW="280px" maxW="320px">
                        {/* Station Header */}
                        <Box
                            bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                            color="white"
                            p={3}
                            borderRadius="lg"
                            mb={3}
                            position="relative"
                        >
                            {/* Favorite Button */}
                            <Box
                                position="absolute"
                                top={2}
                                right={2}
                                cursor="pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFavorite(station);
                                }}
                                p={1}
                                transition="all 0.2s"
                                _hover={{ transform: "scale(1.2)" }}
                            >
                                <Icon
                                    as={stationIsFavorite ? TbStarFilled : TbStar}
                                    boxSize={5}
                                    color={stationIsFavorite ? "yellow.300" : "whiteAlpha.700"}
                                />
                            </Box>
                            <Text
                                fontWeight="bold"
                                fontSize="sm"
                                lineHeight="1.3"
                                m="0 !important"
                                pr={8}
                                textAlign="center"
                            >
                                {station.full_name
                                    ? station.full_name
                                    : `${station.city} / ${station.state}`}
                            </Text>
                            <Text fontSize="xs" opacity={0.8} m="0 !important" textAlign="center">
                                {station.provider} - {station.identifier}
                            </Text>
                        </Box>

                        {/* Weather Data Grid - Centered */}
                        <Flex flexWrap="wrap" gap={2} mb={3} justifyContent="center">
                            {station.last_data?.temp_min != null && (
                                <Box bg="blue.50" p={2} borderRadius="lg" textAlign="center" minW="80px" flex="0 0 auto">
                                    <Text fontWeight="bold" color="blue.600" fontSize="md" m="0 !important">
                                        {station.last_data.temp_min}º
                                    </Text>
                                    <Text fontSize="xs" color="blue.500" m="0 !important">Mínima</Text>
                                </Box>
                            )}
                            {station.last_data?.temp != null && (
                                <Box bg="gray.100" p={2} borderRadius="lg" textAlign="center" minW="80px" flex="0 0 auto">
                                    <Text fontWeight="bold" color="gray.700" fontSize="md" m="0 !important">
                                        {station.last_data.temp}º
                                    </Text>
                                    <Text fontSize="xs" color="gray.600" m="0 !important">Atual</Text>
                                </Box>
                            )}
                            {station.last_data?.temp_max != null && (
                                <Box bg="red.50" p={2} borderRadius="lg" textAlign="center" minW="80px" flex="0 0 auto">
                                    <Text fontWeight="bold" color="red.500" fontSize="md" m="0 !important">
                                        {station.last_data.temp_max}º
                                    </Text>
                                    <Text fontSize="xs" color="red.400" m="0 !important">Máxima</Text>
                                </Box>
                            )}
                            {station.last_data?.wind_speed != null && (
                                <Box bg="green.50" p={2} borderRadius="lg" textAlign="center" minW="80px" flex="0 0 auto">
                                    <Text fontWeight="bold" color="green.600" fontSize="sm" m="0 !important">
                                        {station.last_data.wind_speed} km/h
                                    </Text>
                                    <Text fontSize="xs" color="green.500" m="0 !important">Vento</Text>
                                </Box>
                            )}
                            {station.last_data?.wind_dir != null && (
                                <Box bg="green.50" p={2} borderRadius="lg" textAlign="center" minW="80px" flex="0 0 auto">
                                    <Text fontWeight="bold" color="green.600" fontSize="sm" m="0 !important">
                                        {station.last_data.wind_dir}º ({degreesToCardinal(station.last_data.wind_dir)})
                                    </Text>
                                    <Text fontSize="xs" color="green.500" m="0 !important">Direção</Text>
                                </Box>
                            )}
                            {station.last_data?.wind_gust != null && (
                                <Box bg="green.50" p={2} borderRadius="lg" textAlign="center" minW="80px" flex="0 0 auto">
                                    <Text fontWeight="bold" color="green.600" fontSize="sm" m="0 !important">
                                        {station.last_data.wind_gust} km/h
                                    </Text>
                                    <Text fontSize="xs" color="green.500" m="0 !important">Rajada</Text>
                                </Box>
                            )}
                            {station.last_data?.humidity != null && (
                                <Box bg="cyan.50" p={2} borderRadius="lg" textAlign="center" minW="80px" flex="0 0 auto">
                                    <Text fontWeight="bold" color="cyan.600" fontSize="md" m="0 !important">
                                        {station.last_data.humidity}%
                                    </Text>
                                    <Text fontSize="xs" color="cyan.500" m="0 !important">Umidade</Text>
                                </Box>
                            )}
                            {station.last_data?.precip_rate != null && (
                                <Box bg="blue.50" p={2} borderRadius="lg" textAlign="center" minW="80px" flex="0 0 auto">
                                    <Text fontWeight="bold" color="blue.600" fontSize="sm" m="0 !important">
                                        {station.last_data.precip_rate} mm
                                    </Text>
                                    <Text fontSize="xs" color="blue.500" m="0 !important">Chuva</Text>
                                </Box>
                            )}
                            {station.last_data?.precip_total != null && (
                                <Box bg="blue.50" p={2} borderRadius="lg" textAlign="center" minW="80px" flex="0 0 auto">
                                    <Text fontWeight="bold" color="blue.600" fontSize="sm" m="0 !important">
                                        {station.last_data.precip_total} mm
                                    </Text>
                                    <Text fontSize="xs" color="blue.500" m="0 !important">Acumulado</Text>
                                </Box>
                            )}
                        </Flex>

                        {/* Footer */}
                        <Box borderTop="1px" borderColor="gray.200" pt={2} textAlign="center">
                            <Text fontSize="xs" color="gray.500" mb={2} m="0 0 8px 0 !important">
                                Atualizado {station.last_data?.created_at}
                            </Text>
                            <Link to={`/estacao/${station.id}`}>
                                <Button
                                    size="sm"
                                    bg="#2D6B3F"
                                    color="white"
                                    _hover={{ bg: "#1F5A2F" }}
                                    rightIcon={<TbExternalLink />}
                                    borderRadius="lg"
                                    w="full"
                                >
                                    Ver mais detalhes
                                </Button>
                            </Link>
                        </Box>
                    </Box>
                </Popup>
            </Marker>
        });
    }, [mapMarkerType, favorites, toggleFavorite, isFavorite]);

    const markerIcons = useCallback(function () {
        const filteredStations = getFilteredStations();
        return mapGroupBehavior == "group" ?
            <MarkerClusterGroup
                maxClusterRadius={50}
                disableClusteringAtZoom={7}
                iconCreateFunction={drawClusterIcon}
            >
                {renderStationMarkersWithFavorites(filteredStations)}
            </MarkerClusterGroup>
            :
            renderStationMarkersWithFavorites(filteredStations)
    }, [mapMarkerType, mapGroupBehavior, stations, favorites, stationFilter, renderStationMarkersWithFavorites, getFilteredStations]);

    return (
        <>
            <link
                rel="stylesheet"
                href="https://unpkg.com/react-leaflet-markercluster/dist/styles.min.css"
            />
            {isLoadingStations && (
                <Flex
                    position={"fixed"}
                    bottom={"60px"}
                    left={0}
                    right={0}
                    height={"20px"}
                    zIndex={99}
                    color={"white"}
                    gridGap={2}
                    justifyContent={"center"}
                    textShadow={"#333 0px 0 5px;"}
                >
                    <Spinner color="blue" /> Atualizando estações...
                </Flex>
            )}

            {/* Modern Map Settings Button */}
            <Box className="map-drawer-btn" zIndex={99}>
                <Flex
                    align="center"
                    gap={2}
                    cursor="pointer"
                    onClick={onOpen}
                    px={3}
                    py={2}
                >
                    <Icon as={TbSettings} boxSize={4} />
                    <Box>
                        <Text fontSize="xs" fontWeight="semibold" lineHeight="1.2">
                            Configurar
                        </Text>
                        <Text fontSize="xs" opacity={0.8} lineHeight="1.2">
                            {getLabel()}
                        </Text>
                    </Box>
                </Flex>
            </Box>

            <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="sm">
                <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                <DrawerContent bg={drawerBgColor} borderRightRadius="2xl" shadow="2xl">
                    {/* Modern Header */}
                    <DrawerHeader
                        bg={modalHeaderBg}
                        color="white"
                        borderTopRightRadius="2xl"
                        py={4}
                    >
                        <Flex align="center" justify="space-between">
                            <HStack spacing={3}>
                                <Flex
                                    align="center"
                                    justify="center"
                                    bg="whiteAlpha.200"
                                    borderRadius="lg"
                                    p={2}
                                >
                                    <Icon as={TbSettings} boxSize={5} />
                                </Flex>
                                <Box>
                                    <Text fontSize="lg" fontWeight="bold">
                                        Configurações
                                    </Text>
                                    <Text fontSize="xs" opacity={0.8} fontWeight="normal">
                                        Personalize a visualização do mapa
                                    </Text>
                                </Box>
                            </HStack>
                            <DrawerCloseButton
                                position="relative"
                                top="auto"
                                right="auto"
                                color="white"
                                _hover={{ bg: "whiteAlpha.200" }}
                            />
                        </Flex>
                    </DrawerHeader>

                    <DrawerBody py={6} px={5}>
                        {/* Data Display Section */}
                        <Box mb={6}>
                            <HStack spacing={2} mb={3}>
                                <Icon as={TbChartDots3} color="brand.500" boxSize={5} />
                                <Text fontWeight="semibold" fontSize="sm" color={sectionTitleColor}>
                                    Dado a ser exibido
                                </Text>
                            </HStack>
                            <Flex className="current-data" gap={2} flexWrap="wrap" {...markerTypeGroup}>
                                {Object.keys(markerOptions).map((value) => {
                                    const radio = markerTypeGetRadioProps({ value })
                                    return (
                                        <RadioCard key={value} {...radio}>
                                            {markerOptions[value]}
                                        </RadioCard>
                                    )
                                })}
                            </Flex>
                        </Box>

                        <Divider my={5} borderColor={dividerColor} />

                        {/* Map Layer Section */}
                        <Box mb={6}>
                            <HStack spacing={2} mb={3}>
                                <Icon as={TbMap} color="brand.500" boxSize={5} />
                                <Text fontWeight="semibold" fontSize="sm" color={sectionTitleColor}>
                                    Imagem do Mapa
                                </Text>
                            </HStack>
                            <VStack spacing={2} {...layerGroup}>
                                {Object.keys(mapLayerOptions).map((value) => {
                                    const radio = layerGetRadioProps({ value })
                                    return (
                                        <RadioCard cardOptions={{ w: "full" }} key={value} {...radio}>
                                            {mapLayerOptions[value]}
                                        </RadioCard>
                                    )
                                })}
                            </VStack>
                        </Box>

                        <Divider my={5} borderColor={dividerColor} />

                        {/* Station Filter Section */}
                        <Box>
                            <HStack spacing={2} mb={3}>
                                <Icon as={TbFilter} color="brand.500" boxSize={5} />
                                <Text fontWeight="semibold" fontSize="sm" color={sectionTitleColor}>
                                    Filtrar Estações
                                </Text>
                            </HStack>
                            <VStack spacing={2} {...filterGroup}>
                                {Object.keys(stationFilterOptions).map((value) => {
                                    const radio = filterGetRadioProps({ value })
                                    return (
                                        <RadioCard cardOptions={{ w: "full" }} key={value} {...radio}>
                                            {stationFilterOptions[value]}
                                        </RadioCard>
                                    )
                                })}
                            </VStack>
                            {stationFilter === 'favorites' && favorites.length === 0 && (
                                <Text fontSize="xs" color="orange.500" mt={2} textAlign="center">
                                    Nenhum favorito adicionado ainda
                                </Text>
                            )}
                        </Box>

                    </DrawerBody>

                    <DrawerFooter
                        borderTop="1px"
                        borderColor={drawerFooterBorder}
                        py={4}
                    >
                        <Button
                            w="full"
                            bg="brand.600"
                            color="white"
                            _hover={{ bg: "brand.700" }}
                            _active={{ bg: "brand.800" }}
                            borderRadius="xl"
                            size="md"
                            onClick={onClose}
                        >
                            Aplicar e Fechar
                        </Button>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>

            {/* Latest Stations Popup - shown once per day */}
            <Modal isOpen={latestStationsModal.isOpen} onClose={handleCloseLatestStationsModal} isCentered size="lg">
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="2xl" overflow="hidden" shadow="2xl" maxH="80vh">
                    <ModalHeader
                        bg={modalHeaderBg}
                        color="white"
                        py={4}
                    >
                        <Flex align="center" gap={3}>
                            <Flex
                                align="center"
                                justify="center"
                                bg="whiteAlpha.200"
                                borderRadius="lg"
                                p={2}
                            >
                                <Icon as={TbMapPin} boxSize={5} />
                            </Flex>
                            <Box>
                                <HStack>
                                    <Text fontSize="lg" fontWeight="bold">Novas Estações</Text>
                                    <Badge
                                        bg="whiteAlpha.200"
                                        color="white"
                                        px={2}
                                        py={0.5}
                                        borderRadius="full"
                                        fontSize="xs"
                                    >
                                        Novidades
                                    </Badge>
                                </HStack>
                                <Text fontSize="xs" fontWeight="normal" opacity={0.8}>
                                    Confira as últimas estações adicionadas
                                </Text>
                            </Box>
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton color="white" _hover={{ bg: "whiteAlpha.200" }} />
                    <ModalBody py={5} px={4} bg={modalBodyBg} overflowY="auto">
                        <VStack spacing={2} align="stretch">
                            {latestStations.map((station) => (
                                <Link to={`/estacao/${station.id}`} key={station.id} onClick={handleCloseLatestStationsModal}>
                                    <Flex
                                        p={3}
                                        borderRadius="xl"
                                        border="1px"
                                        borderColor={stationCardBorder}
                                        bg={stationCardBg}
                                        _hover={{
                                            borderColor: "brand.300",
                                            bg: stationCardHoverBg,
                                            transform: "translateX(4px)",
                                        }}
                                        transition="all 0.2s ease"
                                        justify="space-between"
                                        align="center"
                                    >
                                        <Box>
                                            <Text
                                                fontWeight="semibold"
                                                color={stationNameColor}
                                                fontSize="sm"
                                            >
                                                {station.city}/{station.state}
                                            </Text>
                                            <Text fontSize="xs" color={stationSubtitleColor}>
                                                {station.description || station.provider} - {station.identifier}
                                            </Text>
                                        </Box>
                                        <Badge
                                            colorScheme="gray"
                                            variant="subtle"
                                            fontSize="xs"
                                            borderRadius="full"
                                        >
                                            {moment(station.created_at).format("DD/MM/YYYY")}
                                        </Badge>
                                    </Flex>
                                </Link>
                            ))}
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>

            <MapContainer
                center={center}
                zoom={4}
                style={{
                    position: "fixed",
                    top: isMobile ? "88px" : "56px",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: "100%",
                    zIndex: 0,
                }}
                attributionControl={false}
            >
                {
                    mapLayerType == "default" ?
                        <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="OpenStreetMap" />
                        :
                        <>
                            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="ArcGIS" />
                            <TileLayer
                                url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                                opacity={0.75}
                            />
                        </>
                }

                {markerIcons()}

                {/* Legend */}


                <Flex zIndex={999} position={"absolute"} bottom={0} justify={"center"} align={"center"} w="full">
                    <Box bg={"white"} px={2} py={1} roundedTop="md" roundedBottom={"none"} className="map-legend-container">
                        {
                            markerOptionsColorType[mapMarkerType] == "temperatura" && (
                                <Table w={"auto"} size="sm">
                                    <Tbody>
                                        <Tr>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>-10°C</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>0°C</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>10°C</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>20°C</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>30°C</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>40°C</Td>
                                            <Td py={0} px={0} fontSize={"xs"} colSpan={1} textAlign={"center"}>50°C</Td>
                                        </Tr>
                                        <Tr>
                                            <Td p={2} backgroundColor={corTemperatura(-15)}></Td>
                                            <Td p={2} backgroundColor={corTemperatura(-5)}></Td>
                                            <Td p={2} backgroundColor={corTemperatura(-1)}></Td>
                                            <Td p={2} backgroundColor={corTemperatura(2)}></Td>
                                            <Td p={2} backgroundColor={corTemperatura(7)}></Td>
                                            <Td p={2} backgroundColor={corTemperatura(12)}></Td>
                                            <Td p={2} backgroundColor={corTemperatura(17)}></Td>
                                            <Td p={2} backgroundColor={corTemperatura(22)}></Td>
                                            <Td p={2} backgroundColor={corTemperatura(27)}></Td>
                                            <Td p={2} backgroundColor={corTemperatura(32)}></Td>
                                            <Td p={2} backgroundColor={corTemperatura(37)}></Td>
                                            <Td p={2} backgroundColor={corTemperatura(40)}></Td>
                                            <Td p={2} px={0} backgroundColor={corTemperatura(50)}></Td>
                                        </Tr>
                                    </Tbody>
                                </Table>
                            )
                        }

                        {
                            markerOptionsColorType[mapMarkerType] == "umidade" && (
                                <Table w={"auto"} size="sm">
                                    <Tbody>
                                        <Tr>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>10%</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>30%</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>50%</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>70%</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>90%</Td>
                                        </Tr>
                                        <Tr>
                                            <Td p={2} backgroundColor={corUmidade(10)}></Td>
                                            <Td p={2} backgroundColor={corUmidade(20)}></Td>
                                            <Td p={2} backgroundColor={corUmidade(30)}></Td>
                                            <Td p={2} backgroundColor={corUmidade(40)}></Td>
                                            <Td p={2} backgroundColor={corUmidade(50)}></Td>
                                            <Td p={2} backgroundColor={corUmidade(60)}></Td>
                                            <Td p={2} backgroundColor={corUmidade(70)}></Td>
                                            <Td p={2} backgroundColor={corUmidade(80)}></Td>
                                            <Td p={2} backgroundColor={corUmidade(90)}></Td>
                                            <Td p={2} backgroundColor={corUmidade(100)}></Td>
                                        </Tr>
                                    </Tbody>
                                </Table>
                            )
                        }


                        {
                            markerOptionsColorType[mapMarkerType] == "chuva" && (
                                <Table w={"auto"} size="sm">
                                    <Tbody>
                                        <Tr>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>1mm</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>10mm</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>30mm</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>100mm</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>150mm</Td>
                                        </Tr>
                                        <Tr>
                                            <Td p={2} backgroundColor={corChuva(0)}></Td>
                                            <Td p={2} backgroundColor={corChuva(1)}></Td>
                                            <Td p={2} backgroundColor={corChuva(5)}></Td>
                                            <Td p={2} backgroundColor={corChuva(10)}></Td>
                                            <Td p={2} backgroundColor={corChuva(20)}></Td>
                                            <Td p={2} backgroundColor={corChuva(30)}></Td>
                                            <Td p={2} backgroundColor={corChuva(50)}></Td>
                                            <Td p={2} backgroundColor={corChuva(100)}></Td>
                                            <Td p={2} backgroundColor={corChuva(150)}></Td>
                                            <Td p={2} backgroundColor={corChuva(160)}></Td>
                                        </Tr>
                                    </Tbody>
                                </Table>
                            )
                        }

                        {
                            markerOptionsColorType[mapMarkerType] == "vento" && (
                                <Table w={"auto"} size="sm">
                                    <Tbody>
                                        <Tr>
                                            <Td py={0} fontSize={"xs"} colSpan={1} textAlign={"center"}>0km/h</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={1} textAlign={"center"}>5km/h</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={1} textAlign={"center"}>10km/h</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={1} textAlign={"center"}>20km/h</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={1} textAlign={"center"}>30km/h</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={1} textAlign={"center"}>50km/h +</Td>
                                        </Tr>
                                        <Tr>
                                            <Td p={2} backgroundColor={corVento(0)}></Td>
                                            <Td p={2} backgroundColor={corVento(5)}></Td>
                                            <Td p={2} backgroundColor={corVento(10)}></Td>
                                            <Td p={2} backgroundColor={corVento(20)}></Td>
                                            <Td p={2} backgroundColor={corVento(30)}></Td>
                                            <Td p={2} backgroundColor={corVento(50)}></Td>
                                        </Tr>
                                    </Tbody>
                                </Table>
                            )
                        }

                        {
                            mapMarkerType == "elevation" && (
                                <Table w={"auto"} size="sm">
                                    <Tbody>
                                        <Tr>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>20m</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>100m</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>200m</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>600m</Td>
                                            <Td py={0} fontSize={"xs"} colSpan={2} textAlign={"center"}>1000m</Td>
                                        </Tr>
                                        <Tr>
                                            <Td p={2} backgroundColor={corElevacao(0)}></Td>
                                            <Td p={2} backgroundColor={corElevacao(20)}></Td>
                                            <Td p={2} backgroundColor={corElevacao(50)}></Td>
                                            <Td p={2} backgroundColor={corElevacao(100)}></Td>
                                            <Td p={2} backgroundColor={corElevacao(200)}></Td>
                                            <Td p={2} backgroundColor={corElevacao(400)}></Td>
                                            <Td p={2} backgroundColor={corElevacao(600)}></Td>
                                            <Td p={2} backgroundColor={corElevacao(800)}></Td>
                                            <Td p={2} backgroundColor={corElevacao(1000)}></Td>
                                            <Td p={2} backgroundColor={corElevacao(1100)}></Td>
                                        </Tr>
                                    </Tbody>
                                </Table>
                            )
                        }
                    </Box>
                </Flex>

            </MapContainer>
        </>
    );
}


function RadioCard(props) {
    const { getInputProps, getCheckboxProps } = useRadio(props)

    const input = getInputProps()
    const checkbox = getCheckboxProps()

    const bgColor = useColorModeValue("gray.50", "gray.700")
    const borderColor = useColorModeValue("gray.200", "gray.600")
    const hoverBg = useColorModeValue("gray.100", "gray.600")

    var extra = input.checked ? { className: "checked-data" } : {};

    return (
        <Box as='label' flexGrow={1} textAlign="center" {...props.cardOptions} {...extra}>
            <input {...input} />
            <Box
                {...checkbox}
                fontSize="sm"
                cursor="pointer"
                borderWidth="1.5px"
                borderRadius="xl"
                borderColor={borderColor}
                bg={bgColor}
                transition="all 0.2s ease"
                _hover={{
                    bg: hoverBg,
                    borderColor: "brand.300",
                    transform: "translateY(-1px)",
                }}
                _checked={{
                    bg: 'brand.600',
                    color: 'white',
                    borderColor: 'brand.600',
                    shadow: "md",
                }}
                _focus={{
                    boxShadow: 'none',
                    outline: "2px solid",
                    outlineColor: "brand.400",
                    outlineOffset: "2px",
                }}
                px={3}
                py={1.5}
                fontWeight="medium"
            >
                {props.children}
            </Box>
        </Box>
    )
}